import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { FormWrapper } from '@/form-control'
import { CheckboxField, PasswordField, TextField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'
import { ChevronLeft, ChevronRight, CheckCircle2, Quote } from 'lucide-react'

import { AuthToken } from '@/MODELS/types'

import { ApiResponse, api_no_auth, setAccessToken, tryRefreshAccessToken } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import ErrorBoundary from '@/components/ErrorBoundary'

import logo from '../../logo-white-no-bg.svg'

interface IRegistrationForm {
    email: string
    password: string
    confirmPassword: string
    tos: boolean
}

interface RegistrationResponse {
    token?: AuthToken
    confirmationRedirect?: string
    redirectUrl?: string
}

const testimonials = [
    {
        quote: 'Love this. I love this because, it confirms what I\'ve done already for him, and then I get an idea of things I need to do.',
        name: 'April',
        role: 'Parent of 5 year old son with autism',
    },
    {
        quote: 'I like the whole personalization aspect where it is getting to know me and my child, and what we are actually struggling with. It feels like you\'re talking to a therapist.',
        name: 'Cassie',
        role: 'Parent of 5 year old child with autism',
    },
    {
        quote: 'I would give it a 10 out of 10. I think this is incredibly valuable in that it offers so much. From the time when you first have an inkling that your child might be on the spectrum to when they\'re in elementary school, and you\'re looking for advice on an IEP.',
        name: 'Megan',
        role: 'Parent of 7 year old son with autism',
    },
    {
        quote: 'The roadmap made everything feel clearer. It\'s like a plan — and I\'ve never had a plan before.',
        name: 'Joelle',
        role: 'Caregiver for 4 year old child with autism',
    },
    {
        quote: 'I love this because it feels like a long-term plan… I like the progress cards because I\'m able to come back and check, see what I\'ve done.',
        name: 'Faye',
        role: 'Caregiver for 6 year old child with autism',
    },
    {
        quote: 'I love how it breaks things down step-by-step. It makes the whole autism journey feel less scary.',
        name: 'April',
        role: 'Parent of 5 year old son with autism',
    },
    {
        quote: 'This is way more better than Google. All those information are there, and you just click on the one you want and instantly see the information.',
        name: 'Favour',
        role: 'Parent of 7 year old son with autism',
    },
    {
        quote: 'The language is easy to understand — not like those big medical terms that lose me.',
        name: 'Farzana',
        role: 'Parent of 4.5 year old son with autism',
    },
    {
        quote: 'Just seeing that someone built this because they\'ve been through it … it gives me hope.',
        name: 'Kiara',
        role: 'Parent of 4 year old daughter with autism',
    },
    {
        quote: 'It\'s so helpful that the AI knows my child\'s needs — like I don\'t have to explain it all over again.',
        name: 'Shadaria',
        role: 'Parent of 2 year old son with autism',
    },
    {
        quote: 'If I had something like this when my daughter was younger, it would have saved me a lot of hassle and trial and error. When I asked my care manager about Medicaid waivers, they had no information. This tells me that\'s something I should be doing. That\'s huge.',
        name: 'Jennifer',
        role: 'Parent of 22 year old daughter with autism',
    },
    {
        quote: 'All of this is really helpful. Having the ability to choose your own adventure… is very regulating to my nervous system.',
        name: 'Amy',
        role: 'Adult with autism',
    },
    {
        quote: 'I love the intent behind this app, I feel that there\'s people who carry this burden with me.',
        name: 'Camila',
        role: 'Parent of an autistic child',
    },
    {
        quote: 'We\'re still relatively early in the autism journey, so tools that help with clarity and guidance are really, really important to us.',
        name: 'Omar',
        role: 'Parent of a 7 year old child with autism',
    },
    {
        quote: 'BrightPath takes the profile into account, the age into account… so the advice feels specific to your kid.',
        name: 'Ivan',
        role: 'Parent of 5 year old son with autism',
    },
]

export default function RegisterPage() {
    const { fetchUser, user, state } = useAuth()
    const navigate = useNavigate()

    // Set when the submitted email belongs to a coordinator account — the
    // notice links them to the provider portal instead of a plain error.
    const [coordinatorPortalUrl, setCoordinatorPortalUrl] = useState<string | null>(null)
    const formRef = useRef<HTMLDivElement>(null)
    const [activeTestimonial, setActiveTestimonial] = useState(0)
    const [showResumeModal, setShowResumeModal] = useState(false)

    // If user is authenticated, redirect. If mid-onboarding, show resume modal.
    // Only check sessionStorage once auth has resolved to "unauthenticated" — otherwise
    // we can flash the modal during the initial auth-checking window for a logged-in user.
    useEffect(() => {
        if (user) {
            if (!user.onboardingComplete) {
                navigate('/onboarding', { replace: true })
            } else {
                navigate('/', { replace: true })
            }
        } else if (state === 'unauthenticated' && sessionStorage.getItem('brightpath-onboarding')) {
            setShowResumeModal(true)
        }
    }, [user, state, navigate])

    // Handle Safari bfcache — page restored from back button doesn't re-mount React
    useEffect(() => {
        const handler = (e: PageTransitionEvent) => {
            if (e.persisted && sessionStorage.getItem('brightpath-onboarding')) {
                setShowResumeModal(true)
            }
        }
        window.addEventListener('pageshow', handler)
        return () => window.removeEventListener('pageshow', handler)
    }, [])

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
        }, 6000)
        return () => clearInterval(interval)
    }, [])

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <>
        {showResumeModal && (
            <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/60'>
                <div className='w-full max-w-sm mx-4 p-6 bg-white rounded-2xl shadow-xl'>
                    <h3 className='text-lg font-semibold text-gray-900 mb-2'>Resume setup?</h3>
                    <p className='text-sm text-gray-600 mb-5'>
                        It looks like you were in the middle of setting up your account. Would you like to pick up where you left off?
                    </p>
                    <div className='flex gap-3'>
                        <button
                            onClick={() => {
                                sessionStorage.removeItem('brightpath-onboarding')
                                setShowResumeModal(false)
                            }}
                            className='flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50'
                        >
                            Start over
                        </button>
                        <button
                            onClick={async () => {
                                const refreshed = await tryRefreshAccessToken()
                                if (refreshed) {
                                    navigate('/onboarding', { replace: true })
                                } else {
                                    // Session expired — need to log in first
                                    setShowResumeModal(false)
                                }
                            }}
                            className='flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-400'
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        )}
        <main className='min-h-dvh bg-gradient-to-br from-cyan-400 via-cyan-500 to-cyan-600 relative'>
            {/* Floating shapes - desktop only */}
            <div className='absolute inset-0 overflow-hidden'>
                <div className='absolute animate-pulse hidden sm:block top-20 right-32 w-32 h-32 bg-secondary-300/10 rounded-lg rotate-12 animate-float'></div>
                <div className='absolute animate-pulse hidden sm:block top-64 left-20 w-24 h-24 bg-secondary-400/25 rounded-lg -rotate-6 animate-float-delayed'></div>
                <div className='absolute animate-pulse hidden sm:block bottom-32 right-40 w-40 h-40 bg-secondary-300/20 rounded-xl rotate-45 animate-float-slow'></div>
                <div className='absolute animate-pulse hidden sm:block bottom-48 left-1/3 w-28 h-28 bg-brand-400/35 rounded-lg -rotate-12 animate-float'></div>
            </div>

            <div className='relative z-10'>
                {/* Hero Section: Logo + Sign-up Form */}
                <div ref={formRef} className='flex min-h-screen'>
                    <div className='hidden lg:flex lg:flex-1 flex-col justify-center px-12 py-16'>
                        <div className='max-w-lg mx-auto'>
                            <img src={logo} className='w-96 mb-8 animate-jump-in' />
                            <div className='text-white mb-12'>
                                <p className='text-xl text-white/90 leading-relaxed mb-8'>
                                    Empowering families with personalized support for every step of your child's
                                    developmental journey.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className='flex-1 flex flex-col justify-center px-6 py-16 lg:px-12'>
                        <div className='w-full max-w-md mx-auto'>
                            <div className='lg:hidden mb-12 text-center'>
                                <img src={logo} className='h-28 mx-auto relative z-10 mt-2' />
                                <p className='text-white/90 text-md w-72 mx-auto text-wrap'>
                                    Supporting your child's developmental journey
                                </p>
                            </div>

                            <div className='bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 lg:p-10 hover:shadow-3xl transition-all duration-300'>
                                <ErrorBoundary componentName='Auth'>
                                    <h1 className='mb-3 text-3xl font-medium text-center'>Create your account</h1>
                                    <h2 className='mb-5 text-center text-neutral-500'>
                                        Already have an account?{' '}
                                        <a className='text-brand-400 hover:text-brand-500' href='/login'>
                                            Sign In
                                        </a>
                                    </h2>
                                    {coordinatorPortalUrl && (
                                        <div className='w-full mb-4 p-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg'>
                                            This email belongs to a coordinator account.{' '}
                                            <a
                                                href={`${coordinatorPortalUrl}/login`}
                                                className='font-medium underline'
                                            >
                                                Sign in at the provider portal
                                            </a>
                                            .
                                        </div>
                                    )}
                                    <FormWrapper<IRegistrationForm>
                                        endpoint={ROUTES.auth.register}
                                        id={null}
                                        isPublic
                                        preventInitialLoad
                                        onSubmit={async (formValues) => {
                                            setCoordinatorPortalUrl(null)
                                            // Submitted directly (not via FormWrapper's
                                            // internal post) so the coordinator-email
                                            // rejection can render a portal link in place.
                                            const response: ApiResponse<RegistrationResponse> =
                                                await api_no_auth<RegistrationResponse>(
                                                    ROUTES.auth.register,
                                                    {
                                                        email: formValues?.email,
                                                        password: formValues?.password,
                                                    },
                                                    undefined,
                                                    undefined,
                                                    true
                                                )

                                            if (response.error) {
                                                const errData = response.data as
                                                    | { code?: string; portalUrl?: string }
                                                    | undefined
                                                if (errData?.code === 'COORDINATOR_ACCOUNT' && errData.portalUrl) {
                                                    setCoordinatorPortalUrl(errData.portalUrl)
                                                    return
                                                }
                                                throw { status: response.status, error: response.error }
                                            }

                                            try {
                                                if (response.data?.token) {
                                                    setAccessToken(response.data.token)
                                                    await fetchUser()
                                                }
                                            } catch (e) {
                                                console.error('Registration callback error:', e)
                                            }
                                            navigate('/onboarding', { replace: true })
                                        }}
                                        submitButtonText='Get Started'
                                        submitButtonAlignment='center'
                                        validationMode='on-input'
                                        submitButtonClass={'bg-brand-500  hover:bg-brand-400'}
                                        validationRules={[
                                            {
                                                field: 'confirmPassword',
                                                isValid: (fieldValue, formValues) => {
                                                    if (!fieldValue) return true
                                                    return fieldValue === formValues?.password
                                                },
                                                prompt: 'Passwords do not match',
                                            },
                                            {
                                                field: 'email',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    if (!fieldValue) return true
                                                    return /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                                                        fieldValue ?? ''
                                                    )
                                                },
                                                prompt: 'Invalid email address',
                                            },
                                            {
                                                field: 'password',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    return fieldValue?.length && fieldValue?.length >= 8
                                                },
                                                prompt: 'Passwords should be at least 8 characters',
                                            },
                                            {
                                                field: 'password',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    if (!fieldValue) return true
                                                    return /(?=.*[a-z])/.test(fieldValue ?? '')
                                                },
                                                prompt: 'Passwords should contain a lowercase letter',
                                            },
                                            {
                                                field: 'password',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    if (!fieldValue) return true
                                                    return /(?=.*[A-Z])/.test(fieldValue ?? '')
                                                },
                                                prompt: 'Passwords should contain an uppercase letter',
                                            },
                                            {
                                                field: 'password',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    if (!fieldValue) return true
                                                    return /(?=.*\d)/.test(fieldValue ?? '')
                                                },
                                                prompt: 'Passwords should contain a number',
                                            },
                                            {
                                                field: 'password',
                                                isValid: (fieldValue) => {
                                                    if (import.meta.env.MODE === 'development') return true
                                                    if (!fieldValue) return true
                                                    return /(?=.*\W)/.test(fieldValue ?? '')
                                                },
                                                prompt: 'Passwords should contain a special character',
                                            },
                                        ]}
                                        errorMessages={{
                                            500: 'Failed to register. Please retry or refresh the page.',
                                        }}
                                        className='w-full'
                                    >
                                        {(f) => (
                                            <>
                                                <TextField
                                                    required
                                                    {...f('email')}
                                                    autoComplete='email'
                                                    type='email'
                                                    name='email'
                                                />
                                                <PasswordField
                                                    required
                                                    {...f('password')}
                                                    autoComplete='new-password'
                                                    name='new-password'
                                                />
                                                <PasswordField
                                                    required
                                                    {...f('confirmPassword')}
                                                    autoComplete='new-password'
                                                    name='new-password'
                                                />

                                                <div className='flex flex-row gap-x-1 justify-center items-center mt-5'>
                                                    <label
                                                        htmlFor='TermsOfService'
                                                        className='ml-2 text-neutral-500 text-sm'
                                                    >
                                                        By continuing, you agree to our{' '}
                                                        <a
                                                            target='_blank'
                                                            href='/terms-of-service'
                                                            className='text-brand-400 hover:text-brand-500'
                                                        >
                                                            Terms and Conditions
                                                        </a>{' '}
                                                        and{' '}
                                                        <a
                                                            target='_blank'
                                                            href='/privacy-policy'
                                                            className='text-brand-400 hover:text-brand-500'
                                                        >
                                                            {' '}
                                                            Privacy Policy
                                                        </a>
                                                    </label>
                                                </div>
                                            </>
                                        )}
                                    </FormWrapper>
                                </ErrorBoundary>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 1: Roadmap - 3 Steps */}
                <div className='bg-white py-16 px-6 lg:px-12'>
                    <div className='max-w-5xl mx-auto'>
                        <h2 className='text-3xl lg:text-4xl font-bold text-neutral-900 mb-3 text-center'>
                            Your personalized roadmap in three steps.
                        </h2>
                        <p className='text-neutral-500 text-lg mb-12 max-w-2xl mx-auto text-center'>
                            No long forms. No jargon. Just a clear path forward — built around your child.
                        </p>

                        {/* Steps with connector line */}
                        <div className='relative mb-12'>
                            {/* Connector line through all 3 step circles - desktop only */}
                            <div className='hidden md:block absolute top-8 left-[8%] right-[8%] h-0.5 bg-brand-200'></div>

                            <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                                {/* Step 1 */}
                                <div className='relative text-center md:text-left'>
                                    <div className='w-16 h-16 rounded-xl bg-brand-400 text-white font-bold text-xl flex items-center justify-center mx-auto md:mx-0 mb-5 relative z-10'>
                                        01
                                    </div>
                                    <h3 className='text-xl font-bold text-neutral-800 mb-3'>
                                        Tell us about your child and your goals.
                                    </h3>
                                    <p className='text-neutral-500 text-sm leading-relaxed'>
                                        A short, guided assessment helps BrightPath understand where your family is right
                                        now — your child's age, diagnosis, current challenges, and what you most want to
                                        work on.
                                    </p>
                                </div>

                                {/* Step 2 */}
                                <div className='relative text-center md:text-left'>
                                    <div className='w-16 h-16 rounded-xl bg-brand-400 text-white font-bold text-xl flex items-center justify-center mx-auto md:mx-0 mb-5 relative z-10'>
                                        02
                                    </div>
                                    <h3 className='text-xl font-bold text-neutral-800 mb-3'>
                                        Get your personalized care roadmap.
                                    </h3>
                                    <p className='text-neutral-500 text-sm leading-relaxed'>
                                        BrightPath creates a milestone-based, evidence-backed roadmap tailored to your
                                        child. Not a generic plan — your plan. Structured around what matters most to
                                        your family right now.
                                    </p>
                                </div>

                                {/* Step 3 */}
                                <div className='relative text-center md:text-left'>
                                    <div className='w-16 h-16 rounded-xl bg-brand-400 text-white font-bold text-xl flex items-center justify-center mx-auto md:mx-0 mb-5 relative z-10'>
                                        03
                                    </div>
                                    <h3 className='text-xl font-bold text-neutral-800 mb-3'>
                                        Follow one clear next step at a time.
                                    </h3>
                                    <p className='text-neutral-500 text-sm leading-relaxed'>
                                        Each week, BrightPath surfaces the most impactful action you can take — and
                                        guides you through it. As your child grows and your goals evolve, your roadmap
                                        evolves with you.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className='text-center'>
                            <button
                                onClick={scrollToForm}
                                className='inline-flex items-center gap-2 bg-secondary-400 hover:bg-secondary-500 text-neutral-900 font-semibold py-3 px-8 rounded-full text-lg transition-colors duration-200 shadow-lg hover:shadow-xl'
                            >
                                Get your free roadmap
                                <span aria-hidden='true'>&rarr;</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Section 2: Testimonials */}
                <div className='bg-gradient-to-b from-brand-50 to-white py-16 px-6 lg:px-12'>
                    <div className='max-w-4xl mx-auto text-center'>
                        <span className='inline-block bg-secondary-100 text-secondary-600 font-semibold text-sm uppercase tracking-wider px-4 py-1 rounded-full mb-3'>
                            Testimonials
                        </span>
                        <h2 className='text-3xl lg:text-4xl font-bold text-neutral-900 mb-2'>
                            Real Stories <br className='sm:hidden' />
                            <span className='text-secondary-500'>Real Impact</span>
                        </h2>
                        <p className='text-neutral-500 text-lg mb-12 max-w-2xl mx-auto'>
                            Real voices from parents, caregivers, and individuals with autism who have transformed their
                            autism journey with BrightPath.
                        </p>

                        <div className='relative max-w-2xl mx-auto'>
                            <div className='relative bg-white rounded-2xl shadow-lg p-8 pt-12 lg:p-10 lg:pt-14 min-h-[200px] flex flex-col justify-center'>
                                {/* Quote icon - teal rounded square, overlapping top-left */}
                                <div className='absolute -top-6 left-6 lg:left-8 w-14 h-14 rounded-xl bg-brand-400 flex items-center justify-center shadow-md'>
                                    <Quote className='w-7 h-7 text-white' />
                                </div>

                                <p className='text-lg lg:text-xl text-neutral-700 italic leading-relaxed mb-6 text-left'>
                                    "{testimonials[activeTestimonial].quote}"
                                </p>
                                <div className='flex items-center gap-2'>
                                    <div className='w-0.5 h-10 bg-brand-400 rounded-full'></div>
                                    <div className='text-left'>
                                        <p className='font-semibold text-neutral-800'>
                                            {testimonials[activeTestimonial].name}
                                        </p>
                                        <p className='text-sm text-neutral-500'>
                                            {testimonials[activeTestimonial].role}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Navigation arrows */}
                            <button
                                onClick={() =>
                                    setActiveTestimonial(
                                        (prev) => (prev - 1 + testimonials.length) % testimonials.length
                                    )
                                }
                                className='absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-neutral-50 transition-colors'
                                aria-label='Previous testimonial'
                            >
                                <ChevronLeft className='w-5 h-5 text-neutral-600' />
                            </button>
                            <button
                                onClick={() =>
                                    setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
                                }
                                className='absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-neutral-50 transition-colors'
                                aria-label='Next testimonial'
                            >
                                <ChevronRight className='w-5 h-5 text-neutral-600' />
                            </button>

                            {/* Dots */}
                            <div className='flex justify-center gap-2 mt-6'>
                                {testimonials.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveTestimonial(i)}
                                        className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${
                                            i === activeTestimonial ? 'bg-brand-400' : 'bg-neutral-300'
                                        }`}
                                        aria-label={`Go to testimonial ${i + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Closing CTA */}
                <div className='bg-white py-16 px-6 lg:px-12'>
                    <div className='max-w-2xl mx-auto text-center'>
                        <h2 className='text-3xl lg:text-4xl font-bold text-neutral-900 mb-4'>
                            You don't have to figure this out{' '}
                            <span className='text-secondary-500'>alone.</span>
                        </h2>
                        <p className='text-neutral-500 text-lg mb-10 leading-relaxed'>
                            BrightPath was created by parents who have been there. It gives you personalized guidance,
                            clear next steps, and daily support — so you can move forward with confidence, one step at a
                            time.
                        </p>

                        <div className='flex justify-center mb-10'>
                            <div className='flex flex-col items-start gap-4'>
                                <div className='flex items-center gap-3'>
                                    <CheckCircle2 className='w-6 h-6 text-green-500 flex-shrink-0' />
                                    <span className='text-neutral-700 text-lg text-left'>Personalized roadmap for your child</span>
                                </div>
                                <div className='flex items-center gap-3'>
                                    <CheckCircle2 className='w-6 h-6 text-green-500 flex-shrink-0' />
                                    <span className='text-neutral-700 text-lg text-left'>
                                        Daily tips and quick wins to keep you moving forward
                                    </span>
                                </div>
                                <div className='flex items-center gap-3'>
                                    <CheckCircle2 className='w-6 h-6 text-green-500 flex-shrink-0' />
                                    <span className='text-neutral-700 text-lg text-left'>Gentle guidance when you need it</span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={scrollToForm}
                            className='inline-flex items-center gap-2 bg-secondary-400 hover:bg-secondary-500 text-neutral-900 font-semibold py-3 px-8 rounded-full text-lg transition-colors duration-200 shadow-lg hover:shadow-xl mb-4'
                        >
                            Start My BrightPath
                        </button>
                        <p className='text-neutral-400 text-sm'>
                            Free to explore, no credit card required. Then just $4.99/month when you are ready to
                            continue.
                        </p>
                    </div>
                </div>
            </div>
        </main>
        </>
    )
}
